import React, { useCallback } from 'react';
import { capitalize } from 'lodash';
import { Link } from 'react-router-dom';
import { NoDataContentInTable, FormattedAddress, CustomDynamicResultTable, TableBody } from '@css-xp/base';
import { CustomScrollableWrapper } from '@css-xp/base/custom-scrollable-wrapper';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import {
  BillingFrequency,
  CommerceAddress,
  CreditCard,
  Maybe,
  PaymentMethodType,
  PaymentOnTerms,
  ScheduledChanges,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CloudChildProduct, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import {
  CloudProductDetailView,
  getBilledForColumnData,
  LastBilledDate,
  NextBillExpiryDate,
} from '@css-xp/base/entitlement-utils';
import { columns } from './table-columns';
import { isBilledElsewhereProduct } from '@atlassian/cssxp-utils-product';
import { BilledElsewhereBadge } from '../../../../../entitlements-v2/common/components/billed-elsewhere-badge';
import './product-group-table.scss';

const colgroupForInnerTable = (
  <colgroup>
    <col style={{ width: '19.5%' }} />
    <col style={{ width: '10.5%' }} />
    <col style={{ width: '10.5%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '10.5%' }} />
    <col style={{ width: '10.5%' }} />
    <col style={{ width: '9%' }} />
  </colgroup>
);

type ProductGroupTableProps = { products: CloudChildProduct[]; heading?: React.ReactChild };

const ProductGroupTable = ({ products = [], heading }: ProductGroupTableProps) => {
  const getPaymentMethod = (paymentMethod: CreditCard | PaymentOnTerms | null | undefined) => {
    if (paymentMethod) {
      if (paymentMethod?.type === PaymentMethodType.CARD) {
        const creditCard = paymentMethod as CreditCard;
        return <span>{`**** **** **** ${creditCard?.card?.last4Numbers ?? ''}`}</span>;
      } else if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
        const paymentOnTerms = paymentMethod as PaymentOnTerms;
        return <span>Payment on terms: due in {paymentOnTerms.terms?.invoiceDueDays} days</span>;
      }
    }
    return 'Not available';
  };

  const getBillToAddress = (billToParty: CommerceAddress | undefined | null) => {
    if (!billToParty) {
      return 'No address information';
    } else {
      return (
        <span className="line-clamp">
          <FormattedAddress
            addressLine1={billToParty?.addressLine1}
            addressLine2={billToParty?.addressLine2}
            city={billToParty?.city}
            country={billToParty?.country}
            postcode={billToParty?.postcode}
            state={billToParty?.state}
          />
        </span>
      );
    }
  };

  const getCellData = useCallback((header: string, node: CloudChildProduct) => {
    const billedElsewhere = isBilledElsewhereProduct(node?.productId);
    switch (header) {
      case 'Subscription':
        return (
          <CloudProductDetailView
            logoId={node?.logoId}
            productId={node?.productId}
            entitlementNumber={node?.entitlementNumber || ''}
            billingStatus={node?.billingStatus}
            provisioningStatus={node?.provisioningStatus}
            edition={node?.edition}
            productName={node?.productName}
            trial={node?.trial ?? false}
            isSandbox={node?.isSandbox}
            upgradeTrial={node?.upgradeTrial}
            scheduledChanges={node?.scheduledChanges as Maybe<ScheduledChanges> | undefined}
          />
        );
      case 'Billing account': {
        if (billedElsewhere) {
          return <BilledElsewhereBadge className="opsgenie-billing-account" />;
        }
        return (
          <>
            {node?.transactionAccountNumber ? (
              <>
                <Link
                  to={`${TRANSACTION_ACCOUNT_PATH_2}/${node.transactionAccountNumber}`}
                  className="line-clamp line-clamp-name"
                >
                  {node?.transactionAccountName ?? 'Unnamed billing account'}
                </Link>
                <div>{node.transactionAccountNumber}</div>
              </>
            ) : (
              <NoDataContentInTable />
            )}
          </>
        );
      }
      case 'Billing address':
        return !billedElsewhere && getBillToAddress(node?.billToParty);
      case 'Payment method':
        return !billedElsewhere && getPaymentMethod(node?.defaultPaymentMethod);
      case 'Usage':
        if (billedElsewhere) {
          return undefined;
        }
        return (
          <>
            <div>{capitalize(getBilledForColumnData(node?.productId, node?.edition))}</div>
            <div>{node?.activeUsers ?? '0'}</div>
          </>
        );
      case 'Last billed':
        if (billedElsewhere) {
          return undefined;
        }
        return <LastBilledDate item={node} />;
      case 'Next bill/ Expiry':
        if (billedElsewhere) {
          return undefined;
        }
        return <NextBillExpiryDate item={node} />;
      case 'Billing cycle':
        return (
          !billedElsewhere && (
            <>
              <p>{node?.billingCycle ? capitalize(node.billingCycle) : <NoDataContentInTable />}</p>
              {node?.billingCycle === BillingFrequency.ANNUAL && node?.userTier && <p>User tier - {node?.userTier}</p>}
            </>
          )
        );
      case 'Pricing eligibility':
        if (billedElsewhere) {
          return undefined;
        }
        return <>{node?.pricingPlan?.type ? capitalize(node?.pricingPlan?.type) : <NoDataContentInTable />}</>;
      default:
        return 'NA';
    }
  }, []);

  const getRowDataForGroupedProduct = (data: CloudChildProduct[]) => {
    return data.map((product: CloudChildProduct, index: number) => {
      const tableCols = columns.map((col, colIndex) => <td key={colIndex}>{getCellData(col.name, product)}</td>);

      return (
        <tr className="row-separator" key={index}>
          {tableCols}
        </tr>
      );
    });
  };

  if (!products.length) return null;

  return (
    <CustomScrollableWrapper heading={heading}>
      <CustomDynamicResultTable
        isLoading={false}
        error={null}
        testId="product-app-subscriptions"
        emptyStateProps={{
          header: '',
          description: NO_DETAILS_TO_SHOW,
        }}
        isPaginationEnabled={false}
        noResults={false}
        className="product-app-group-table-container"
      >
        {colgroupForInnerTable}
        <TableBody>{getRowDataForGroupedProduct(products)}</TableBody>
      </CustomDynamicResultTable>
    </CustomScrollableWrapper>
  );
};

export default ProductGroupTable;
